import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { queryOptions } from '~components/hooks/_constants';
import { checkValidContractAddress } from '~config/protocol';
import { AllVestaAssets, ALL_ASSET_VALUES, ELPType, EOtherAsset, EVestaAsset } from '~config/types';
import { queryClient } from '~pages/_app';
import { getAllPrices } from '~services/contracts/priceFeed';
import { setInitialState } from '~utils/utils';

import {
  useContractAsset,
  useContractBalPool,
  useContractGLPManager,
  useContractPriceFeed,
  useContractVSTOracle,
} from '../../contracts';
import { useSession } from '../../useSession';
import { AssetPrices } from '../_types';
import { useAllChainlinkContracts } from '../useAllChainlinkContracts';

import { useLPPrices } from './useLPPrices';

export const initialPriceState: AssetPrices = {
  oracle: setInitialState(EVestaAsset, 0),
  market: setInitialState(ALL_ASSET_VALUES, 0),
};

export const AssetPricesQueryKey = 'asset-prices';

export const useSingleAssetPrices = () => {
  const { network } = useSession();
  const { read: priceFeedRead, address: priceFeedAddress } = useContractPriceFeed();
  const { read: vstOracleRead, address: vstOracleAddress } = useContractVSTOracle();
  const allChainlinkContracts = useAllChainlinkContracts();

  const { read: glpManagerRead, address: glpManagerAddress } = useContractGLPManager();
  const { read: glpERC20Read, address: glpERC20Address } = useContractAsset(EOtherAsset.fsGLP);
  const { read: balancerVaultRead, address: balancerVaultAddress } = useContractBalPool();

  const result = useQuery<AssetPrices>(
    [AssetPricesQueryKey, network.networkId],
    async () => {
      try {
        const assetPrices = await getAllPrices({
          priceFeedRead,
          vstOracleRead,
          glpManagerRead,
          glpERC20Read,
          balancerVaultRead,
          allChainlinkContracts,
          network: network.networkId,
        });

        return assetPrices;
      } catch (err) {
        console.error(err);
        return initialPriceState;
      }
    },
    queryOptions(
      true,
      checkValidContractAddress([
        priceFeedAddress,
        vstOracleAddress,
        glpManagerAddress,
        glpERC20Address,
        balancerVaultAddress,
      ]),
    ),
  );

  const pricesData = result.data ?? initialPriceState;

  return { ...result, data: pricesData };
};

export interface AllAssetPrices {
  oracle: { [index in EVestaAsset]: number };
  market: { [index in AllVestaAssets]: number };
  lp: { [index in ELPType]: number };
  all: { [index in AllVestaAssets | ELPType]: number };
}

type UseAssetPricesReturnType = () => {
  data: AllAssetPrices;
  isLoading: boolean;
  isFetched: boolean;
  isFetching: boolean;
  invalidate: () => Promise<void>;
};

const useAssetPrices: UseAssetPricesReturnType = () => {
  const singlePrices = useSingleAssetPrices();
  const lpPrices = useLPPrices();
  const invalidate = () => queryClient.invalidateQueries([AssetPricesQueryKey]);

  return useMemo(
    () => ({
      data: {
        ...singlePrices.data,
        lp: lpPrices.data,
        all: { ...singlePrices.data.oracle, ...singlePrices.data.market, ...lpPrices.data },
      } as AllAssetPrices,
      isLoading: singlePrices.isLoading || lpPrices.isLoading,
      isFetched: singlePrices.isFetched || lpPrices.isFetched,
      isFetching: singlePrices.isFetching || lpPrices.isFetching,
      invalidate,
    }),
    [singlePrices.data, lpPrices.data],
  );
};

export default useAssetPrices;
